import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import { useAppDispatch, useAppSelector } from "store/hooks";
import logo from "icons/smallLogo.svg";
import Footer from "components/layout/footer";
import {
  fetchPaymentDetails,
  paymentDownload,
  paymentReceiptGet,
  updateLogoAndFavicon,
} from "store/slices/paymentsSlice";

import { formatCurrency } from "utils/HelperFunctions";
import PaymentLinkProcessFailed from "components/actionCenter/PaymentLinkProcessFailed";
import PaymentLinkProcessSuccess from "components/actionCenter/PaymentLinkProcessSuccess";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import { RootState } from "store/store";
import { TestIdScope, useTestAttrsFactory } from "utils/testid";
import {
  decodeAndValidatePublicLink,
  isInvalidPublicLinkResult,
} from "utils/publicLinkValidation";
import {
  getViewPaymentOutcome,
  isRetryablePaymentStatus,
  ViewPaymentOutcome,
} from "./paymentOutcome";

type PaymentHistoryDetails = {
  id?: string | number;
  amount?: string | number;
  ach_fee_amount?: string | number;
  cc_fee_amount?: string | number;
  payment_method?: string;
  payment_status?: string;
  email?: string;
  bank_name?: string;
  routing_number?: string;
  account_number?: string;
  swift_code?: string;
  due_date?: string;
  wire_due_date?: string;
};

type WireDetails = {
  email: string;
  bank: string;
  routing: string;
  account: string;
  swift: string;
  dueDate: string;
};

const EMPTY_WIRE_DETAILS: WireDetails = {
  email: "",
  bank: "",
  routing: "",
  account: "",
  swift: "",
  dueDate: "",
};

const firstString = (...values: unknown[]): string => {
  const match = values.find(
    (value) => typeof value === "string" && value.trim().length > 0
  );
  return typeof match === "string" ? match.trim() : "";
};

// The deadline can arrive as a database timestamp; show it as a plain date,
// the way the screen right after setting up the wire does.
// Only the date part of the text is used: turning it into a Date first would
// shift a midnight-UTC deadline to the previous day in time zones behind UTC.
const formatWireDueDate = (value: string): string => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})[T ]/.exec(value);
  if (!parts) return value;
  return `${parts[2]}/${parts[3]}/${parts[1]}`;
};

const getWireDetails = (
  payment: PaymentHistoryDetails,
  customer: any,
  receiptData?: any
): WireDetails => {
  const receiptPayment =
    receiptData?.paymentData || receiptData?.orderData || {};
  const receiptCustomer = receiptData?.getCustomerData || {};
  const pendingWire = Array.isArray(receiptData?.clientsScheduleCustomData)
    ? receiptData.clientsScheduleCustomData.find(
        (item: any) => item?.status === "Pending"
      )
    : undefined;

  return {
    email: firstString(
      payment.email,
      customer?.email,
      receiptPayment?.email,
      receiptCustomer?.email
    ),
    bank: firstString(
      payment.bank_name,
      receiptPayment?.bank_name,
      receiptCustomer?.bank_name
    ),
    routing: firstString(
      payment.routing_number,
      receiptPayment?.routing_number,
      receiptCustomer?.routing_number
    ),
    account: firstString(
      payment.account_number,
      receiptPayment?.account_number,
      receiptCustomer?.account_number
    ),
    swift: firstString(
      payment.swift_code,
      receiptPayment?.swiftCode,
      receiptPayment?.swift_code,
      receiptCustomer?.swift_code
    ),
    dueDate: formatWireDueDate(
      firstString(
        payment.due_date,
        payment.wire_due_date,
        receiptPayment?.due_date,
        receiptPayment?.wire_due_date,
        pendingWire?.billing_date
      )
    ),
  };
};

const hasRequiredWireDetails = (details: WireDetails): boolean =>
  Boolean(details.bank && details.routing && details.account);

const RetryPaymentContent = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const testAttrs = useTestAttrsFactory();
  const [accountData, setAccountData] = useState<any>(null);
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { id: paymentId } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [paymentLogo, setPaymentLogo] = useState<string>(logo);
  const [paymentOutcome, setPaymentOutcome] =
    useState<ViewPaymentOutcome | null>(null);
  const [paymentDetails, setPaymentDetails] =
    useState<PaymentHistoryDetails | null>(null);
  const [wireDetails, setWireDetails] =
    useState<WireDetails>(EMPTY_WIRE_DETAILS);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [showDetails, setShowDetails] = useState<boolean>(true);

  const getPaymentDetails = async () => {
    setIsLoading(true);
    setLoadError(false);
    setPaymentOutcome(null);
    setPaymentDetails(null);
    setWireDetails(EMPTY_WIRE_DETAILS);

    const publicLink = decodeAndValidatePublicLink(paymentId, "pid_");
    if (!publicLink) {
      goToInvalid(navigate, "link");
      setIsLoading(false);
      return;
    }

    setInvoiceId(publicLink.recordId);

    try {
      let params = {
        slug: paymentId,
      };
      const res: any = await dispatch(fetchPaymentDetails(params)).unwrap();

      if (isInvalidPublicLinkResult(res)) {
        goToInvalid(navigate, "link");
        return;
      }

      if (res?.body?.success) {
        const { paymentHistoryDetails, companyLogoDetails, customerData } =
          res?.body?.data;
        const details = (paymentHistoryDetails || {}) as PaymentHistoryDetails;
        const method = details.payment_method?.toUpperCase();
        const fee =
          method === "ACH"
            ? Number(details.ach_fee_amount) || 0
            : ["CC", "CAPP", "GP", "AP", "AMZP"].includes(method || "")
              ? Number(details.cc_fee_amount) || 0
              : 0;
        const outcome = getViewPaymentOutcome(
          details.payment_status,
          details.payment_method
        );

        setAmount((Number(details.amount) || 0) + fee);
        setPaymentDetails(details);
        setPaymentOutcome(outcome);
        setPaymentLogo(companyLogoDetails?.logo || logo);
        dispatch(updateLogoAndFavicon(companyLogoDetails));
        setAccountData({
          lead_id: customerData?.lead_id,
          ...customerData?.data,
        });

        if (outcome === "wire-pending") {
          let nextWireDetails = getWireDetails(details, customerData?.data);

          if (!hasRequiredWireDetails(nextWireDetails)) {
            try {
              const receiptResponse: any = await dispatch(
                paymentReceiptGet({
                  id: publicLink.recordId,
                  pid: paymentId,
                  source: "dc_orders",
                })
              ).unwrap();
              nextWireDetails = getWireDetails(
                details,
                customerData?.data,
                receiptResponse?.body?.data
              );
            } catch {
              console.error("Unable to load wire instructions");
            }
          }

          setWireDetails(nextWireDetails);
          if (!hasRequiredWireDetails(nextWireDetails)) {
            setLoadError(true);
          }
        }
      } else {
        setLoadError(true);
      }
    } catch (error) {
      if (isInvalidPublicLinkResult(error)) {
        goToInvalid(navigate, "link");
        return;
      }
      setLoadError(true);
      console.error("Unable to load payment details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getPaymentDetails();
  }, [paymentId]);

  //send email
  const sendEmail = async ({ emails, shouldSendAll }: any) => {
    try {
      const validEmails = Array.isArray(emails)
        ? emails
            .map((email: any) =>
              typeof email === "string" ? email.trim() : ""
            )
            .filter(Boolean)
        : [];

      if (!invoiceId || !accountData?.accountid || validEmails.length === 0) {
        console.warn("Receipt email skipped: missing required values");
        return;
      }

      let payload = {
        invoice_id: invoiceId,
        accountid: accountData?.accountid,
        emails: validEmails,
        shouldSendAll: shouldSendAll,
      };
      await dispatch(paymentDownload(payload)).unwrap();
    } catch {
      console.error("Unable to send receipt email");
    }
  };

  return (
    <div
      className={`lg:grid grid-cols-2 lg:h-full ${
        false ? "h-[calc(var(--shell-vh)-78px)]" : "h-[var(--shell-vh)]"
      } overflow-y-scroll`}
      {...testAttrs("page", "Retry payment")}
    >
      <div
        style={{ backgroundColor: brandColor }}
        className="lg:h-full lg:max-h-[var(--shell-vh)] lg:overflow-y-scroll lg:py-[90px] py-5 clusterId relative"
        {...testAttrs("details-panel", "Retry payment details")}
      >
        <div className="lg:max-w-[380px] w-full mx-auto flex flex-col items-start lg:gap-10 gap-5 px-5">
          <div className="flex items-center gap-[10px]">
            {isLoading ? (
              <Skeleton width={100} height={35} />
            ) : (
              <div className="h-16 lg:h-16 flex justify-end">
                <img
                  src={paymentLogo}
                  alt="Company logo"
                  className="animate__animated animate__fadeIn object-contain"
                />
              </div>
            )}
          </div>
          <div className="w-full">
            <span className="w-full flex justify-between items-center">
              <div
                className={`${
                  isDark ? "text-darktxtParagraph" : "text-txtParagraph"
                } font-bold text-xl leading-[26.04px]`}
              >
                Make Payment
              </div>
              <div
                className={`lg:hidden text-base font-medium underline ${
                  isDark
                    ? "text-darkTxtBlue hover:!text-darkBlueHover"
                    : "text-blue hover:!text-btnPrimaryHover"
                } cursor-pointer`}
                role="button"
                tabIndex={0}
                {...testAttrs(
                  "toggle-details",
                  showDetails
                    ? "Hide retry payment details"
                    : "View retry payment details"
                )}
                onClick={() => setShowDetails(!showDetails)}
              >
                {showDetails ? "Hide details" : "View details"}
              </div>
            </span>{" "}
            <p
              className={`font-bold lg:leading-[65.1px] leading-[41.66px] lg:mt-4 mt-6 ${
                isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
              } lg:text-[44px] text-[32px]`}
            >
              {isLoading ? (
                <Skeleton width={150} height={70} />
              ) : (
                formatCurrency(amount) || "$0.00"
              )}
            </p>
          </div>
          <span
            className={`drawer ${
              showDetails && "open"
            } flex flex-col w-full gap-5`}
            {...testAttrs("account-details", "Retry payment account details")}
          >
            <div className="w-full h-[1px] bg-strokeActive"></div>
            <div className="w-full">
              <p
                className={`flex justify-between mb-4  ${
                  isDark ? "text-darkTxtBlue" : "text-blue "
                } `}
              >
                {(accountData?.first_name || "") +
                  " " +
                  (accountData?.last_name || "")}
                <span>{accountData?.accountid}</span>
              </p>

              <p
                className={`flex justify-between ${
                  isDark ? "text-darkTxtSecondary" : "text-txtSecondary"
                } text-sm`}
              >
                Balance <span>Description</span>
              </p>

              <p
                className={`flex justify-between ${
                  isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                } text-sm`}
              >
                {isLoading ? (
                  <Skeleton width={45} height={15} />
                ) : (
                  formatCurrency(accountData?.balance) || "$0.00"
                )}
                <span>
                  {isLoading ? (
                    <Skeleton width={45} height={15} />
                  ) : (
                    accountData?.description || "--"
                  )}{" "}
                </span>
              </p>
            </div>
          </span>
          <Footer
            style={{ backgroundColor: brandColor }}
            className={`!justify-start !w-[30%] lg:flex hidden xl:flex-nowrap flex-wrap lg:max-w-[380px] ${
              isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
            }`}
          />
        </div>
      </div>
      <div
        className="h-full lg:max-h-[var(--shell-vh)] lg:overflow-y-scroll px-5"
        {...testAttrs("result-panel", "Retry payment result")}
      >
        <div className="h-full flex justify-center">
          {isLoading ? (
            <div
              className="h-full max-h-[var(--shell-vh)] flex justify-center items-center"
              {...testAttrs("loading", "Loading retry payment")}
            >
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
                aria-hidden="true"
              ></div>
            </div>
          ) : (
            <div
              className={`lg:max-w-[510px] flex flex-col lg:gap-10 gap-6  text-LightTextParagraph mt-2 lg:w-[98%] w-full`}
              {...testAttrs("result-content", "Retry payment result content")}
            >
              {loadError || !paymentOutcome || !paymentDetails ? (
                <PaymentLinkProcessFailed
                  isRetry={true}
                  title="Payment details unavailable"
                  failedReason="We couldn't load the payment details. Please try again later."
                  showUpdatePaymentMethod={false}
                  showDeclinedMessage={false}
                />
              ) : paymentOutcome === "wire-pending" ? (
                <WirePaymentSuccess
                  bankDetails={{
                    ...wireDetails,
                    email: wireDetails.email || accountData?.email || "",
                    swift: wireDetails.swift || "--",
                  }}
                  isRetry={true}
                  sendEmail={sendEmail}
                  orderId={invoiceId}
                  lead_id={accountData?.lead_id}
                  className=""
                  showSuccessToast
                />
              ) : paymentOutcome === "success" ? (
                <PaymentLinkProcessSuccess
                  email={accountData?.email || paymentDetails.email || ""}
                  amount={amount}
                  invoiceId={invoiceId}
                  sendEmail={sendEmail}
                  lead_id={accountData?.lead_id}
                  className="!px-0"
                  openDrawer={true}
                  closePreviewLocally={true}
                />
              ) : (
                <PaymentLinkProcessFailed
                  failedReason="This payment was not completed. Please update your payment method and try again."
                  handleBackToStart={() =>
                    navigate(`/payments/failed/${paymentId}`)
                  }
                  isRetry={true}
                  showUpdatePaymentMethod={isRetryablePaymentStatus(
                    paymentDetails.payment_status
                  )}
                  showDeclinedMessage={false}
                />
              )}
              <div></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RetryPayment = () => (
  <TestIdScope scope="retry-payment">
    <RetryPaymentContent />
  </TestIdScope>
);

export default RetryPayment;
